import { useBusinessContext } from '@root/context'
import { fetchTag, ITag } from '@root/query/tags'
import React, { useState } from 'react'
import { keepPreviousData, useQueries } from '@tanstack/react-query'
import styled from 'styled-components'
import { AnimatedContentLoader } from '../../components/AnimatedContentLoader'
import { CreateTagPill, TagPill } from '../../components/TagPill'
import { TagFormModal } from '@root/pages/SettingsPage/sections/TagsSettings/TagFormModal'
import DocumentTagSelectorDropDown from './DocumentTagSelectorDropDown'
import { useTagSelectorFloating } from './useTagSelectorFloating'
import { useTranslation } from 'react-i18next'

export interface TagSelectorProps {
  value?: number[]
  onChange?: (tagIds: number[]) => void
  label?: string
  isOnlySelect?: boolean
  isReadOnly?: boolean
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  value,
  onChange,
  label,
  isOnlySelect = false,
  isReadOnly = false
}) => {
  const [isTagFormModalOpen, setIsTagFormModalOpen] = useState(false)
  const [selectedTag, setSelectedTag] = useState<ITag | undefined>(undefined)
  const fallbackFocusRef = React.useRef<HTMLButtonElement>(null)

  const { t } = useTranslation()
  const { slug } = useBusinessContext()

  const tagsResults = useQueries({
    queries: (value || []).map(tagId => ({
      queryKey: [slug, 'tags', tagId],
      queryFn: () => fetchTag({ slug, tagId }),
      placeholderData: keepPreviousData,
      staleTime: 60 * 1000
    }))
  })
  const tags = tagsResults.filter(result => result?.data).map(result => result.data)

  const {
    isDropDownOpen,
    toggleDropDown,
    handleOnSelect,
    context,
    refs,
    getReferenceProps,
    getFloatingProps
  } = useTagSelectorFloating({
    value,
    onChange,
    focusRef: fallbackFocusRef,
    shouldRetainFocus: () => isTagFormModalOpen
  })

  const handleOnDismiss = (tagId: number) => {
    const newTagIds = value?.filter(id => id !== tagId) || []
    onChange?.(newTagIds)
  }

  const onTagFormModalClose = () => {
    setIsTagFormModalOpen(false)
    fallbackFocusRef.current?.focus()
  }

  const onEditTag = (tag: ITag) => {
    setIsTagFormModalOpen(true)
    setSelectedTag(tag)
  }

  const onCreateTag = () => {
    setSelectedTag(undefined)
    setIsTagFormModalOpen(true)
  }

  return (
    <>
      <TagFormModal
        isVisible={isTagFormModalOpen}
        handleOnClose={onTagFormModalClose}
        onCreated={handleOnSelect}
        tag={selectedTag}
      />

      {label && <StyledLabel>{label}</StyledLabel>}

      <StyledContainer {...getReferenceProps()} ref={refs.setReference}>
        <AnimatedContentLoader isLoading={false}>
          <StyledTagContainer>
            {tags.map(tag => (
              <TagPill
                key={tag.id}
                tag={tag}
                onDisMiss={(tagId: number) => handleOnDismiss(tagId)}
                focusAfterDismiss={fallbackFocusRef}
                maxWidth={'100%'}
              />
            ))}

            {!isReadOnly && (
              <CreateTagPill
                ref={fallbackFocusRef}
                onClick={toggleDropDown}
                text={tags.length > 0 ? undefined : t('components.tagPill.addNew')}
              />
            )}
          </StyledTagContainer>
        </AnimatedContentLoader>

        {isDropDownOpen && (
          <DocumentTagSelectorDropDown
            context={context}
            getFloatingProps={getFloatingProps}
            refs={refs}
            onTagSelect={handleOnSelect}
            isSelected={tag => Boolean(value?.includes(tag.id))}
            onEditTag={onEditTag}
            onCreateTag={onCreateTag}
            isOnlySelect={isOnlySelect}
          />
        )}
      </StyledContainer>
    </>
  )
}

const StyledTagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const StyledContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: fit-content;
`

const StyledLabel = styled.label`
  display: flex;
  white-space: nowrap;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: ${({ theme }) => theme.fontSize.md}rem;
  margin: 0;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
  margin-top: ${({ theme }) => theme.spacing.md}rem;

  & > * {
    align-self: center;
  }
`
